import { EMPTY, Observable, concat, defer, from, of } from 'rxjs';
import { catchError, filter, finalize, shareReplay, switchMap, tap } from 'rxjs/operators';
import { CacheService, CacheStoreName, CachedValue } from '../services/cache.service';

export interface CachedEndpointOptions<T> {
  /** IndexedDB bo'limi. */
  store: CacheStoreName;
  /** Bo'lim ichidagi kalit. Tenant qo'shimchasi avtomatik qo'shiladi. */
  key: string;
  /** Serverdan olish. Faqat kerak bo'lganda chaqiriladi. */
  fetch: () => Observable<T>;
  /**
   * Shu vaqt ichida kesh "yangi" hisoblanadi va so'rov UMUMAN yuborilmaydi.
   * Ma'lumot qanchalik tez eskirsa, shuncha kichik bo'ladi.
   */
  freshMs: number;
  /** Shundan keyin kesh butunlay tashlanadi va faqat tarmoqdan olinadi. */
  maxAgeMs: number;
  /**
   * Kalitning bo'limi (odatda tenant id). `null` — hamma uchun umumiy.
   * Har o'qishda qaytadan chaqiriladi: biznes almashtirilsa kalit ham almashadi.
   */
  scope: () => string | null;
  /**
   * Yangi javob eskisidan farq qiladimi. Farq qilmasa ikkinchi qiymat
   * CHIQARILMAYDI — pastdagi izohga qarang.
   */
  equals?: (a: T, b: T) => boolean;
}

/**
 * Bitta GET endpointning keshlangan ko'rinishi: eski ma'lumotni darhol beradi,
 * kerak bo'lsa orqa fonda yangilaydi.
 *
 * <h3>Nima uchun kerak</h3>
 * <p>
 * Do'konlar yoki kategoriyalar kabi lug'atlarni o'nga yaqin ekran ochilishida
 * so'raydi. Har safar tarmoqni kutish — foydalanuvchi uchun bo'sh dropdown, server
 * uchun esa deyarli hech qachon o'zgarmaydigan javobni qayta-qayta yuborish.
 * </p>
 *
 * <h3>O'qish qoidasi</h3>
 * <ol>
 *   <li>Kesh bor va yangi (`freshMs`) — so'rov YUBORILMAYDI, bitta qiymat chiqadi.</li>
 *   <li>Kesh bor, lekin eskirgan — darhol eski qiymat chiqadi, so'ng so'rov ketadi.</li>
 *   <li>Kesh yo'q — odatdagidek so'rov.</li>
 * </ol>
 *
 * <h3>Ikkinchi qiymat faqat O'ZGARSA chiqadi</h3>
 * <p>
 * Bu qasddan qo'yilgan qoida. Chaqiruvchilarning bir qismi javob kelganda boshqa
 * yuklashni boshlaydi (masalan `orders` — do'konlar kelgach hisobotni so'raydi).
 * Har eskirgan o'qishda ikkita bir xil qiymat chiqarilsa, ular ikki barobar so'rov
 * yuborar va kesh tarmoqni kamaytirish o'rniga ko'paytirib qo'yardi. Ro'yxat
 * haqiqatan o'zgargandagina ikkinchi qiymat chiqadi — u holda qayta yuklash to'g'ri.
 * </p>
 */
export class CachedEndpoint<T> {
  /**
   * Sessiya ichidagi nusxa. IndexedDB asinxron: u bo'lmasa har o'qish kamida bitta
   * mikrotask kechikardi va bir vaqtda ochilgan ikki ekran keshni ikki marta o'qirdi.
   */
  private memory: { key: string; value: CachedValue<T> } | null = null;

  /**
   * Ayni damda ketayotgan so'rov. Ilova ochilganda `StoreContextService` va sahifa
   * bir vaqtda so'raydi — ular bitta so'rovni bo'lishadi.
   */
  private inFlight: { key: string; request: Observable<T> } | null = null;

  /**
   * `invalidate()` sanog'i. So'rov ketayotganda kesh bekor qilinsa, kelgan javob
   * ALLAQACHON eskirgan bo'ladi — uni yozib qo'yish o'zgartirilgan ma'lumot ustiga
   * eski nusxani qaytarardi.
   */
  private generation = 0;

  /**
   * Kesh oxirgi marta qachon bekor qilingani.
   *
   * <p>
   * Faqat `cache.delete()` ga tayanib bo'lmaydi: u asinxron, ya'ni bekor qilingandan
   * keyingi birinchi o'qish o'chirilishidan OLDIN saqlangan yozuvni ko'rib ulgurishi
   * mumkin. Shu sanadan oldin yozilgan har qanday nusxa — ta'rifiga ko'ra eskirgan,
   * chunki `invalidate()` server o'zgarishni tasdiqlagach chaqiriladi. Tekshiruv
   * sinxron, shuning uchun hech qanday poyga qolmaydi.
   * </p>
   */
  private invalidatedAt = 0;

  constructor(
    private readonly cache: CacheService,
    private readonly options: CachedEndpointOptions<T>,
  ) {}

  private scopedKey(): string {
    return `${this.options.key}::${this.options.scope() ?? 'global'}`;
  }

  private isSame(a: T, b: T): boolean {
    return this.options.equals
      ? this.options.equals(a, b)
      : JSON.stringify(a) === JSON.stringify(b);
  }

  /**
   * Ma'lumotni o'qiydi. Obuna bo'lingandagina ishlaydi (`defer`), shuning uchun
   * kalit obuna paytidagi tenantga qarab hisoblanadi.
   */
  read(): Observable<T> {
    return defer(() => {
      const key = this.scopedKey();

      const inMemory = this.memory?.key === key ? this.memory.value : null;
      if (inMemory) return this.serve(key, inMemory);

      return from(this.cache.get<T>(this.options.store, key)).pipe(
        switchMap((persisted) => {
          if (!persisted || persisted.savedAt <= this.invalidatedAt) return this.revalidate(key);
          this.memory = { key, value: persisted };
          return this.serve(key, persisted);
        }),
      );
    });
  }

  /** Keshni bekor qiladi — keyingi o'qish serverdan oladi. */
  invalidate(): void {
    this.reset();
    void this.cache.delete(this.options.store, this.scopedKey());
  }

  /**
   * Faqat xotiradagi holatni tashlaydi (IndexedDB ga tegmaydi).
   * `ApiCacheService.clearAll()` bo'limlarni o'zi butunlay tozalagach chaqiriladi.
   */
  reset(): void {
    this.generation++;
    this.invalidatedAt = Date.now();
    this.memory = null;
    this.inFlight = null;
  }

  private serve(key: string, cached: CachedValue<T>): Observable<T> {
    if (Date.now() - cached.savedAt < this.options.freshMs) return of(cached.data);

    // Eskirgan: avval eski qiymat, keyin — o'zgargan bo'lsa — yangisi.
    // Bu yerdagi so'rov xatosi YUTILADI: ekranda yaroqli ro'yxat turibdi va uning
    // ustiga qizil toast chiqarish foydalanuvchini bekorga qo'rqitardi. Kesh
    // umuman bo'lmaganda esa xato o'tib ketadi — ko'rsatadigan narsa yo'q.
    return concat(
      of(cached.data),
      this.revalidate(key).pipe(
        filter((fresh) => !this.isSame(fresh, cached.data)),
        catchError(() => EMPTY),
      ),
    );
  }

  private revalidate(key: string): Observable<T> {
    if (this.inFlight?.key === key) return this.inFlight.request;

    const generation = this.generation;
    const request = this.options.fetch().pipe(
      tap((data) => {
        if (generation !== this.generation) return;
        this.memory = { key, value: { data, savedAt: Date.now() } };
        void this.persist(key, data, generation);
      }),
      finalize(() => {
        if (this.inFlight?.key === key) this.inFlight = null;
      }),
      // `refCount: false`: obunachi ketsa ham so'rov bekor qilinmaydi. Ekran
      // yopilgan bo'lsa ham javob keshga tushsin — keyingi ochilishda tayyor turadi.
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.inFlight = { key, request };
    return request;
  }

  /**
   * Javobni saqlashga yozadi.
   *
   * <p>
   * Yozishning O'ZI asinxron (IndexedDB tranzaksiyasi), shuning uchun `invalidate()`
   * shu oraliqda tushishi mumkin: do'kon tahrirlanadi, kesh o'chiriladi — va bir
   * necha mikrotaskdan keyin oldin boshlangan yozuv eski nusxani QAYTA tiriltiradi.
   * Yozuv tugagach sanoq qaytadan tekshiriladi va kech qolgan yozuv darhol
   * o'chiriladi; `invalidate()` ni kutib turish esa uni sekinlashtirardi.
   * </p>
   */
  private async persist(key: string, data: T, generation: number): Promise<void> {
    await this.cache.set(this.options.store, key, data, this.options.maxAgeMs);
    if (generation !== this.generation) {
      await this.cache.delete(this.options.store, key);
    }
  }
}
